// src/pages/Adjudicaciones.tsx

import React, { useState, useEffect } from "react";
import { fetchAdjudicaciones } from "../api/adjudicacionesApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../css/concursos.css";
import "../css/checkboxdropdown.css";
import "../css/amountdropdown.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faLandmark,
  faLink,
  faMapMarkerAlt,
  faCheck,
  faSpinner,
  faFileExcel,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

import { Link, useSearchParams } from "react-router-dom";
import CheckboxDropdown from "../components/checkboxDropdown";
import AmountDropdown from "../components/amountDropdown";
import LoadingLottie from "../components/LoadingLottie";
import { getAuthToken } from "../security/AuthLogin";

const Adjudicaciones: React.FC = () => {
  const [adjudicaciones, setAdjudicaciones] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Filtros
  const [pendingSearchTerm, setPendingSearchTerm] = useState<string>(
    searchParams.get("search") || ""
  );
  const [pendingTipoConcurso, setPendingTipoConcurso] = useState<string[]>(
    searchParams.getAll("tipo") || []
  );
  const [pendingFechaRango, setPendingFechaRango] = useState<
    [Date | null, Date | null]
  >([
    searchParams.get("fechaInicio")
      ? new Date(searchParams.get("fechaInicio")!)
      : null,
    searchParams.get("fechaFin")
      ? new Date(searchParams.get("fechaFin")!)
      : null,
  ]);
  const [pendingImporte, setPendingImporte] = useState<{
    desde: string;
    hasta: string;
  }>({
    desde: searchParams.get("importeDesde") || "",
    hasta: searchParams.get("importeHasta") || "",
  });
  const [pendingOrdenarPor, setPendingOrdenarPor] = useState<string>(
    searchParams.get("ordenarPor") || ""
  );

  const [pendingAdjudicatarioName, setPendingAdjudicatarioName] =
    useState<string>(searchParams.get("adjudicatarioName") || "");
  const [pendingAdjudicatarioNif, setPendingAdjudicatarioNif] =
    useState<string>(searchParams.get("adjudicatarioNif") || "");

  // Nuevos checkboxes
  const [pendingPresentado, setPendingPresentado] = useState<boolean>(
    searchParams.get("presentado") === "true"
  );
  const [pendingGanado, setPendingGanado] = useState<boolean>(
    searchParams.get("ganado") === "true"
  );

  // Extraer parámetros actuales
  const searchTerm = searchParams.get("search") || "";
  const tipoConcurso = searchParams.getAll("tipo");
  const fechaRango: [string, string] = [
    searchParams.get("fechaInicio") || "",
    searchParams.get("fechaFin") || "",
  ];
  const importeDesde = searchParams.get("importeDesde") || "";
  const importeHasta = searchParams.get("importeHasta") || "";
  const ordenarPor = searchParams.get("ordenarPor") || "";
  const adjudicatarioNameFilter = searchParams.get("adjudicatarioName") || "";
  const adjudicatarioNifFilter = searchParams.get("adjudicatarioNif") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  // Opciones de tipo
  const tipoConcursoOptions: string[] = [
    "Obras",
    "Servicios",
    "Suministros",
    "Mixto",
    "Concesión de obras",
    "Concesión de servicios",
  ];

  // Estado para el spinner/tick en el botón "Descargar Excel"
  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  const loadAdjudicaciones = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number | string[]> = {
        page,
        limit: 10,
        search: searchTerm,
        fechaInicio: fechaRango[0],
        fechaFin: fechaRango[1],
        importeDesde,
        importeHasta,
        ordenarPor,
      };

      if (tipoConcurso.length > 0) {
        params.tipo = tipoConcurso;
      }
      if (adjudicatarioNameFilter) {
        params.adjudicatarioName = adjudicatarioNameFilter;
      }
      if (adjudicatarioNifFilter) {
        params.adjudicatarioNif = adjudicatarioNifFilter;
      }
      if (searchParams.get("presentado") === "true") {
        params.presentado = "true";
      }
      if (searchParams.get("ganado") === "true") {
        params.ganado = "true";
      }

      const { data, total, limit } = await fetchAdjudicaciones(params);
      setAdjudicaciones(data);
      setTotalPages(Math.ceil(total / limit));
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error desconocido al cargar las adjudicaciones.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdjudicaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Aplicar filtros
  const applyFilters = () => {
    const params: Record<string, string | string[]> = {};

    if (pendingSearchTerm.trim()) params["search"] = pendingSearchTerm.trim();
    if (pendingTipoConcurso.length > 0) params["tipo"] = pendingTipoConcurso;

    if (pendingFechaRango[0]) {
      params["fechaInicio"] = new Date(
        pendingFechaRango[0].getTime() -
          pendingFechaRango[0].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }
    if (pendingFechaRango[1]) {
      params["fechaFin"] = new Date(
        pendingFechaRango[1].getTime() -
          pendingFechaRango[1].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }
    if (pendingImporte.desde) params["importeDesde"] = pendingImporte.desde;
    if (pendingImporte.hasta) params["importeHasta"] = pendingImporte.hasta;
    if (pendingOrdenarPor) params["ordenarPor"] = pendingOrdenarPor;

    // ★ NUEVO: añadir adjudicatario
    if (pendingAdjudicatarioName) {
      params["adjudicatarioName"] = pendingAdjudicatarioName;
    }
    if (pendingAdjudicatarioNif) {
      params["adjudicatarioNif"] = pendingAdjudicatarioNif;
    }

    if (pendingPresentado) {
      params["presentado"] = "true";
    }
    if (pendingGanado) {
      params["ganado"] = "true";
    }

    params["page"] = "1";
    setSearchParams(params);
  };

  // Manejo de checkboxes
  const handlePresentadoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingPresentado(e.target.checked);
  };
  const handleGanadoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingGanado(e.target.checked);
  };

  const handleFilterReset = () => {
    setPendingSearchTerm("");
    setPendingTipoConcurso([]);
    setPendingFechaRango([null, null]);
    setPendingImporte({ desde: "", hasta: "" });
    setPendingOrdenarPor("");
    setPendingPresentado(false);
    setPendingGanado(false);
    setPendingAdjudicatarioName("");
    setPendingAdjudicatarioNif("");

    setSearchParams({});
  };

  const changePage = (newPage: number) => {
    const newParams: Record<string, string | string[]> = {};
    // Copiar todos los parámetros actuales excepto 'page'
    searchParams.forEach((value, key) => {
      if (key !== "page") {
        if (key === "tipo") {
          if (!newParams["tipo"]) {
            newParams["tipo"] = [];
          }
          (newParams["tipo"] as string[]).push(value);
        } else {
          newParams[key] = value;
        }
      }
    });

    newParams["page"] = `${newPage}`;
    setSearchParams(newParams);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    })
      .format(value)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // Lógica para spinner/tick en botón Excel
  const downloadExcel = async () => {
    try {
      // Spinner ON
      setExcelStatus("loading");

      const token = getAuthToken();
      if (!token) {
        alert("No hay token de autenticación, por favor inicie sesión.");
        setExcelStatus("idle");
        return;
      }

      const queryString = searchParams.toString();
      const exportUrl = `${
        `${window.location.protocol}//${window.location.hostname}`
      }/api/adjudicaciones/export?${queryString}`;

      const response = await fetch(exportUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error en la descarga: ${response.statusText}`);
      }

      // Blob
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "adjudicaciones.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      // Descarga exitosa
      setExcelStatus("success");
      // Pasados 2s, revertir a idle
      setTimeout(() => {
        setExcelStatus("idle");
      }, 2000);
    } catch (err) {
      console.error("Error al descargar el Excel:", err);
      alert("Ocurrió un error al descargar el Excel.");
      setExcelStatus("idle");
    }
  };

  const renderExcelButtonContent = () => {
    if (excelStatus === "loading") {
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
          Generando...
        </>
      );
    }
    if (excelStatus === "success") {
      return (
        <>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          ¡Descargado!
        </>
      );
    }
    // Estado idle
    return (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );
  };

  return (
    <div className="container-concursos">
      <div className="ordenar-por">
        <label htmlFor="ordenarPor">Ordenar por:</label>
        <select
          id="ordenarPor"
          value={pendingOrdenarPor}
          onChange={(e) => setPendingOrdenarPor(e.target.value)}
          className="form-control"
        >
          <option value="">Seleccione...</option>
          <option value="fecha_publicacion ASC">
            Fecha de publicación (ascendente)
          </option>
          <option value="fecha_publicacion DESC">
            Fecha de publicación (descendente)
          </option>
          <option value="fecha_fin_ofertas ASC">
            Fecha de fin de ofertas (ascendente)
          </option>
          <option value="fecha_fin_ofertas DESC">
            Fecha de fin de ofertas (descendente)
          </option>
          <option value="importe_total ASC">Importe total (ascendente)</option>
          <option value="importe_total DESC">
            Importe total (descendente)
          </option>
        </select>
        <button onClick={applyFilters} className="btn btn-primary">
          Aplicar
        </button>
      </div>

      <div className="filtros">
        <input
          type="text"
          placeholder="Buscar por título, descripción o expediente..."
          value={pendingSearchTerm}
          onChange={(e) => setPendingSearchTerm(e.target.value)}
          className="form-control busqueda"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
        />

        <input
          type="text"
          placeholder="Nombre adjudicatario"
          value={pendingAdjudicatarioName}
          onChange={(e) => setPendingAdjudicatarioName(e.target.value)}
          className="form-control busqueda"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
        />
        <input
          type="text"
          placeholder="NIF adjudicatario"
          value={pendingAdjudicatarioNif}
          onChange={(e) => setPendingAdjudicatarioNif(e.target.value)}
          className="form-control busqueda"
          style={{ marginTop: 4 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
        />

        <CheckboxDropdown
          options={tipoConcursoOptions}
          selectedOptions={pendingTipoConcurso}
          onChange={(selected) => setPendingTipoConcurso(selected)}
          placeholder="Seleccione tipo(s) de concurso"
        />

        <AmountDropdown
          desde={pendingImporte.desde}
          hasta={pendingImporte.hasta}
          onChange={(desde, hasta) => setPendingImporte({ desde, hasta })}
          placeholder="Importe"
        />

        <DatePicker
          selected={pendingFechaRango[0] || undefined}
          onChange={(dates: [Date | null, Date | null]) =>
            setPendingFechaRango(dates)
          }
          startDate={pendingFechaRango[0] || undefined}
          endDate={pendingFechaRango[1] || undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Rango de fechas"
        />

        {/* Checkboxes Presentadas/Ganadas */}
        <div className="checkbox-filtro">
          <label>
            <input
              type="checkbox"
              checked={pendingPresentado}
              onChange={handlePresentadoChange}
            />
            Presentadas
          </label>
        </div>

        <div className="checkbox-filtro">
          <label>
            <input
              type="checkbox"
              checked={pendingGanado}
              onChange={handleGanadoChange}
            />
            Ganadas
          </label>
        </div>

        <button
          onClick={applyFilters}
          className="btn btn-primary"
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
        >
          Filtrar
        </button>
        <button onClick={handleFilterReset} className="btn btn-secondary">
          Eliminar filtros
        </button>
      </div>

      <div className="bulto"></div>

      {/* Botón para descargar Excel con spinner/tick */}
      <div className="boton-descarga" style={{ margin: "20px 0" }}>
        <button
          onClick={downloadExcel}
          className="btn btn-success"
          disabled={excelStatus === "loading"} // Deshabilita mientras 'loading'
        >
          {renderExcelButtonContent()}
        </button>
      </div>

      {loading ? (
        <LoadingLottie />
      ) : error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : adjudicaciones.length === 0 ? (
        <p>No hay adjudicaciones disponibles.</p>
      ) : (
        <div className="listing-container">
          {adjudicaciones.map((adjudicacion) => {
            const importeAdjudicacion =
              parseFloat(adjudicacion.importe_adjudicacion) || 0;
            const importeTotal = parseFloat(adjudicacion.importe_total) || 0;
            const importeEstimado =
              parseFloat(adjudicacion.importe_estimado) || 0;

            // ————— normalizar adjudicatarios —————
            let rawList: { name?: string; nif?: string }[] = [];

            if (Array.isArray(adjudicacion.adjudicatario)) {
              // varios lotes → cada item.adjudicatario
              rawList = adjudicacion.adjudicatario.map(
                (item: any) => item.adjudicatario || item
              );
            } else if (adjudicacion.adjudicatario?.adjudicatario) {
              // nested → un solo objeto dentro de .adjudicatario.adjudicatario
              rawList = [adjudicacion.adjudicatario.adjudicatario];
            } else if (
              adjudicacion.adjudicatario?.nif ||
              adjudicacion.adjudicatario?.name
            ) {
              // flat → un solo objeto en .adjudicatario
              rawList = [adjudicacion.adjudicatario];
            }

            // ————— construir texto final —————
            let nombreAdjudicatario = "No disponible";
            if (rawList.length > 1) {
              nombreAdjudicatario = "Múltiples ganadores";
            } else if (rawList.length === 1) {
              const a = rawList[0];
              if (a.name) {
                nombreAdjudicatario = a.name + (a.nif ? ` (${a.nif})` : "");
              } else {
                nombreAdjudicatario = a.nif || "No disponible";
              }
            }
            // —————————————————————————

            let importeMostrado = "N/A";

            if (importeAdjudicacion !== 0) {
              importeMostrado = formatCurrency(importeAdjudicacion);
            } else if (importeTotal !== 0) {
              importeMostrado = formatCurrency(importeTotal);
            } else if (importeEstimado !== 0) {
              importeMostrado = formatCurrency(importeEstimado);
            }

            return (
              <div className="listing-item" key={adjudicacion.id_concurso}>
                <div className="info">
                  <div className="header-concurso">
                    <div className="expediente">
                      {adjudicacion.codigo_expediente}
                    </div>
                    <div className="subheader-concurso">
                      <div
                        className={`estado estado-${adjudicacion.estado
                          .toLowerCase()
                          .replace(/ /g, "-")}`}
                      >
                        {adjudicacion.estado || "Sin especificar"}
                      </div>
                      <div className="publication-date">
                        {new Date(
                          adjudicacion.fecha_publicacion
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="title">{adjudicacion.titulo}</div>
                  <div className="details">
                    <div>
                      <FontAwesomeIcon icon={faLandmark} className="icon" />{" "}
                      {adjudicacion.entidad_compradora}
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="icon" />{" "}
                      {adjudicacion.pais} - {adjudicacion.localidad}
                    </div>
                  </div>
                  {/* <div className="extra-info">
                    {["En plazo", "Anuncio previo"].includes(
                      adjudicacion.estado
                    ) &&
                      adjudicacion.fecha_fin_ofertas && (
                        <div className="fin-plazo">
                          <strong>Hasta </strong>
                          <span>
                            {`${new Date(
                              adjudicacion.fecha_fin_ofertas
                            ).toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })} - ${new Date(
                              adjudicacion.fecha_fin_ofertas
                            ).toLocaleTimeString("es-ES", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}h`}
                          </span>
                          <span className="dias-restantes">
                            {(() => {
                              const diffDays = Math.ceil(
                                (new Date(
                                  adjudicacion.fecha_fin_ofertas
                                ).getTime() -
                                  new Date().getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );
                              if (diffDays === 0) {
                                return ` (Hoy)`;
                              } else if (diffDays < 0) {
                                return ` (Terminado)`;
                              } else {
                                return ` (En ${diffDays} día${
                                  diffDays > 1 ? "s" : ""
                                })`;
                              }
                            })()}
                          </span>
                        </div>
                      )}
                  </div> */}
                  <div
                    className="details adjudicatario"
                    style={{ marginTop: 8 }}
                  >
                    <FontAwesomeIcon icon={faUser} className="icon" />{" "}
                    <strong>Adjudicatario:</strong> &nbsp; {nombreAdjudicatario}
                  </div>
                </div>
                <div className="footer-concurso">
                  <div className="importe">{importeMostrado}</div>
                  <div className="actions-concurso">
                    <Link to={`/adjudicacion/${adjudicacion.id_concurso}`}>
                      <FontAwesomeIcon icon={faEye} title="Ver más" />
                    </Link>
                    <a
                      href={adjudicacion.enlace}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Enlace oficial"
                    >
                      <FontAwesomeIcon icon={faLink} />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="pagination-concursos">
        <button
          onClick={() => changePage(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          Anterior
        </button>
        <span>
          Página {page} de {totalPages}
        </span>
        <button
          onClick={() => changePage(Math.min(page + 1, totalPages))}
          disabled={page === totalPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default Adjudicaciones;
